import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ClipboardCheck, AlertTriangle, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";

const classLabel = (c) => (["LKG", "UKG", "Play Group"].includes(c) ? c : `${c}`);

const ratioTone = (submitted, total) => {
  if (!total) return "text-slate-400";
  const pct = (submitted / total) * 100;
  return pct < 50 ? "text-rose-600 dark:text-rose-400" : pct < 100 ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-300";
};

export function PracticeTestsModal({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/admin/practice-tests/detail")
      .then(({ data }) => setData(data))
      .catch((e) => { toast.error(apiErr(e.response?.data?.detail) || "Could not load practice tests"); setData(null); })
      .finally(() => setLoading(false));
  }, [open]);

  const tests = data?.tests || [];
  const noTest = data?.classes_without_tests || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="practice-tests-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <span className="h-8 w-8 rounded-lg bg-sky-50 dark:bg-sky-900/40 text-sky-600 dark:text-sky-300 flex items-center justify-center">
              <ClipboardCheck className="h-4 w-4" />
            </span>
            Practice Tests
          </DialogTitle>
          <DialogDescription>
            {loading ? "Loading…" : `${tests.length} test${tests.length === 1 ? "" : "s"} given · ${noTest.length} class${noTest.length === 1 ? "" : "es"} pending`}
          </DialogDescription>
        </DialogHeader>

        {/* Classes with no test yet — top banner */}
        {!loading && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3" data-testid="pt-no-test-banner">
            {noTest.length === 0 ? (
              <div className="text-sm inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-300">
                <ShieldCheck className="h-4 w-4" /> Every class has at least one test.
              </div>
            ) : (
              <>
                <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5 inline-flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 text-rose-500" /> Classes with no test yet ({noTest.length})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {noTest.map((c) => (
                    <span
                      key={`${c.class_name}-${c.section}`}
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800"
                      data-testid={`pt-no-test-${c.class_name}-${c.section}`}
                    >
                      {classLabel(c.class_name)}-{c.section}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Test list */}
        <div className="overflow-y-auto -mx-1 px-1 mt-1" data-testid="pt-test-list">
          {loading ? (
            <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
          ) : tests.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">No practice tests have been given yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {tests.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-3 py-3" data-testid={`pt-row-${t.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300">
                        {classLabel(t.class_name)}-{t.section}
                      </span>
                      <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{t.subject || t.title}</span>
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                      {t.title}{t.teacher_name ? ` · by ${t.teacher_name}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-lg font-bold ${ratioTone(t.submitted, t.total)}`} style={{ fontFamily: "Outfit" }}>
                      {t.submitted}<span className="text-slate-400 font-normal">/{t.total}</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 inline-flex items-center gap-1 justify-end">
                      <Users className="h-3 w-3" /> submitted
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
